import { useState, useCallback, useEffect } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { ModeContext, type AppMode } from './ModeContext';
import { getLastMode, setLastMode } from '../utils/prefStorage';
import { deriveModeFromPath } from '../utils/deriveMode';

export const ModeProvider = ({ children }: { children: React.ReactNode }) => {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const derivedMode = deriveModeFromPath(pathname);

  // Sticky mode for routes that don't imply a mode (settings, activity, etc.).
  const [stickyMode, setStickyMode] = useState<AppMode>(
    () => getLastMode() ?? 'season'
  );

  // When the URL implies a mode, remember it so ambiguous routes visited later
  // keep the right mode.
  useEffect(() => {
    if (derivedMode && derivedMode !== stickyMode) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect -- Preserve existing behavior while enabling the rule globally.
      setStickyMode(derivedMode);
      setLastMode(derivedMode);
    }
  }, [derivedMode, stickyMode]);

  const setMode = useCallback((newMode: AppMode) => {
    setStickyMode(newMode);
    setLastMode(newMode);
  }, []);

  // The URL is the source of truth where it implies a mode; otherwise fall back
  // to the last chosen mode.
  const mode = derivedMode ?? stickyMode;

  return <ModeContext value={{ mode, setMode }}>{children}</ModeContext>;
};
